"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Philosophy() {
  const container = useRef<HTMLDivElement>(null);
  const headlineWords = [{
    text: "I",
    italic: false
  }, {
    text: "build",
    italic: false
  }, {
    text: "what",
    italic: false
  }, {
    text: "growth",
    italic: false
  }, {
    text: "demands.",
    italic: false
  }, {
    text: "I",
    italic: false
  }, {
    text: "shape",
    italic: false
  }, {
    text: "what",
    italic: false
  }, {
    text: "search",
    italic: true,
    marker: true
  }, {
    text: "requires.",
    italic: true,
    marker: true
  }];
  const paragraphs = ["I focus on sustainable results rather than quick fixes. The technical foundation stays robust; the strategy stays clear.", "My methodology pairs detailed technical diagnostics with search intent analysis, testing and refining each step based on real performance.", "I tailor every strategy to your business goals because generic tactics rarely deliver real ROI."];
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".philo-word", {
        y: 40
      });
      gsap.to(".philo-word", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.07,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.to(".philo-marker-path", {
        strokeDashoffset: 0,
        duration: 0.6,
        ease: "power2.out",
        delay: 0.9,
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".philo-kicker", {
        y: 12
      });
      gsap.to(".philo-kicker", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".philo-block", {
        y: 20
      });
      gsap.to(".philo-block", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          once: true
        }
      });
      gsap.set(".philo-cta", {
        y: 12
      });
      gsap.to(".philo-cta", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 60%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".philo-word", ".philo-kicker", ".philo-block", ".philo-cta"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".philo-marker-path", {
        strokeDashoffset: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="30673" id="the-expert-behind-the-strategy" className="relative w-full overflow-hidden bg-background text-foreground">
      <div ref={container} className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-[88px]" style={{
      paddingTop: "clamp(128px, 16vh, 200px)",
      paddingBottom: "clamp(128px, 16vh, 200px)"
    }}>
        <div className="flex justify-center">
          <span className="philo-kicker font-sans text-center uppercase font-medium text-muted-foreground" style={{
          opacity: 0,
          fontSize: "clamp(0.825rem, 0.9vw, 0.925rem)",
          letterSpacing: "0.18em",
          lineHeight: 1
        }}>
            MY APPROACH
          </span>
        </div>

        <h2 className="font-serif mx-auto text-center mt-[128px]" style={{
        fontSize: "clamp(1.825rem, 2.8vw, 2.4rem)",
        fontWeight: 400,
        letterSpacing: "-0.028em",
        lineHeight: 0.98,
        maxWidth: "960px",
        color: "hsl(240 9% 13%)"
      }}>
          {headlineWords.map((w, i) => <span key={i} data-index={i} className="philo-word inline-block mr-[0.28em] align-baseline relative" style={{
          opacity: 0
        }}>
              <span className="relative inline-block" style={{
            fontStyle: w.italic ? "italic" : "normal"
          }}>
                {w.text}
                {w.marker && <svg aria-hidden="true" className="absolute left-0 pointer-events-none" viewBox="0 0 200 14" preserveAspectRatio="none" style={{
              bottom: "-0.08em",
              width: "100%",
              height: "0.24em",
              overflow: "visible"
            }}>
                    <path className="philo-marker-path" d="M2 8 Q 40 3, 90 6 T 198 7" fill="none" stroke="hsl(220 80% 30%)" strokeWidth="6" strokeLinecap="round" style={{
                strokeDasharray: 260,
                strokeDashoffset: 260
              }} />
                  </svg>}
              </span>
            </span>)}
        </h2>

        <div className="mx-auto flex flex-col items-center" style={{
        maxWidth: "960px",
        marginTop: "96px"
      }}>
          {paragraphs.map((text, i) => <div key={i} data-index={i} className="w-full flex flex-col items-center">
              <p className="philo-block font-sans w-full text-left text-foreground" style={{
            opacity: 0,
            fontSize: "clamp(1.25rem, 1.35vw, 1.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55,
            maxWidth: "58ch"
          }}>
                {text}
              </p>

              {i < paragraphs.length - 1 && <div aria-hidden="true" className="philo-block flex justify-center items-center" style={{
            opacity: 0,
            marginTop: "48px",
            marginBottom: "48px"
          }}>
                  <span className="inline-block" style={{
              width: "8px",
              height: "8px",
              transform: "rotate(45deg)",
              backgroundColor: "hsl(220 80% 30%)"
            }} />
                </div>}
            </div>)}
        </div>

        <div className="philo-cta flex justify-center" style={{
        opacity: 0,
        marginTop: "128px"
      }}>
          <Link to="/ServicesPage" className="group inline-flex items-center gap-3 font-sans font-medium relative text-foreground" style={{
          fontSize: "1.0625rem",
          letterSpacing: "0.02em"
        }}>
            <span className="relative pb-1">
              Explore my services
              <span aria-hidden="true" className="absolute left-0 bottom-0 h-px w-full origin-left scale-x-0 transition-transform duration-[260ms] ease-out group-hover:scale-x-100" style={{
              backgroundColor: "hsl(220 80% 30%)"
            }} />
            </span>
            <ArrowRight className="transition-transform duration-[220ms] ease-out group-hover:translate-x-[6px]" style={{
            width: "18px",
            height: "18px",
            strokeWidth: 1.25
          }} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>;
}